import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type Accessory } from '@/services/accessories/types';

/**
 * The visitor's accessory order list ("cart" for parts, mirroring the car
 * quotation list). Accessories are line items that can be ordered in quantity,
 * but the list itself only tracks which parts are on it — quantities and fitment
 * are settled on the phone, as with cars.
 *
 * Kept on the visitor's own device so it survives a refresh. It holds only
 * what the list needs to render; prices and availability are re-read from the
 * API when the list is shown.
 */
export type AccessoryOrderItem = {
  id: string;
  slug: string;
  name: string;
  addedAt: string;
  quantity: number;
};

export const MAX_ACCESSORY_QUANTITY = 99;

const clampQuantity = (n: number) => Math.min(MAX_ACCESSORY_QUANTITY, Math.max(1, Math.floor(n) || 1));

/** Enough parts for a real enquiry, few enough to keep the request small. */
export const MAX_ACCESSORY_ORDER = 12;

type AccessoryOrderState = {
  items: AccessoryOrderItem[];
  add: (accessory: Accessory) => void;
  remove: (slug: string) => void;
  toggle: (accessory: Accessory) => void;
  clear: () => void;
  increment: (slug: string) => void;
  decrement: (slug: string) => void;
  setQuantity: (slug: string, quantity: number) => void;
  /** Puts back items removed by remove/clear (undo), keeping their original order. */
  restore: (items: AccessoryOrderItem[]) => void;
  has: (slug: string) => boolean;
  isFull: () => boolean;
};

export const useAccessoryOrderStore = create<AccessoryOrderState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (accessory) => {
        const { items } = get();
        if (items.length >= MAX_ACCESSORY_ORDER || items.some((i) => i.slug === accessory.slug)) return;
        set({
          items: [
            ...items,
            {
              id: accessory.id,
              slug: accessory.slug,
              name: accessory.name,
              addedAt: new Date().toISOString(),
              quantity: 1,
            },
          ],
        });
      },
      remove: (slug) => set({ items: get().items.filter((i) => i.slug !== slug) }),
      toggle: (accessory) =>
        get().has(accessory.slug) ? get().remove(accessory.slug) : get().add(accessory),
      clear: () => set({ items: [] }),
      increment: (slug) => get().setQuantity(slug, (get().items.find((i) => i.slug === slug)?.quantity ?? 1) + 1),
      decrement: (slug) => get().setQuantity(slug, (get().items.find((i) => i.slug === slug)?.quantity ?? 1) - 1),
      setQuantity: (slug, quantity) =>
        set({ items: get().items.map((i) => (i.slug === slug ? { ...i, quantity: clampQuantity(quantity) } : i)) }),
      restore: (restored) => {
        const current = get().items;
        const known = new Set(current.map((i) => i.slug));
        const merged = [...current, ...restored.filter((i) => !known.has(i.slug))]
          .sort((a, b) => a.addedAt.localeCompare(b.addedAt))
          .slice(0, MAX_ACCESSORY_ORDER);
        set({ items: merged });
      },
      has: (slug) => get().items.some((i) => i.slug === slug),
      isFull: () => get().items.length >= MAX_ACCESSORY_ORDER,
    }),
    {
      name: 'kabex.accessory-order',
      version: 2,
      migrate: (persisted) => {
        const state = persisted as { items?: Partial<AccessoryOrderItem>[] };
        return {
          items: (state.items ?? []).map((i) => ({ ...i, quantity: clampQuantity(i.quantity ?? 1) })),
        } as Pick<AccessoryOrderState, 'items'>;
      },
      partialize: (state) => ({ items: state.items }),
    },
  ),
);